import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AudioLines } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/auth/auth-context';

export function AcceptInvitePage(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [name, setName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const lookup = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.auth.lookupInvite(token),
    enabled: token.length > 0,
    retry: false,
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await api.auth.acceptInvite({ token, name, password });
      await refresh();
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not accept the invite');
    } finally {
      setSubmitting(false);
    }
  };

  const invalidReason =
    token.length === 0
      ? 'This link is missing its invite token.'
      : lookup.data && !lookup.data.valid
        ? lookup.data.reason === 'expired'
          ? 'This invite has expired — ask a super admin to send a new one.'
          : lookup.data.reason === 'already_used'
            ? 'This invite has already been used. Try signing in instead.'
            : 'This invite link is not valid.'
        : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <AudioLines className="h-5 w-5" />
            <CardTitle>Set up your account</CardTitle>
          </div>
          <CardDescription>
            {lookup.data?.email ? `Invited as ${lookup.data.email}` : 'Choose a name and password.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invalidReason ? (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>{invalidReason}</AlertDescription>
              </Alert>
              <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
                Go to sign in
              </Button>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
              <div className="space-y-2">
                <Label htmlFor="name">Your name</Label>
                <Input
                  id="name"
                  required
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={submitting || lookup.isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  minLength={10}
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={submitting || lookup.isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  minLength={10}
                  required
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                  disabled={submitting || lookup.isLoading}
                />
              </div>

              {error ? (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}

              <Button type="submit" className="w-full" disabled={submitting || lookup.isLoading}>
                {submitting ? 'Creating account…' : 'Create account'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
